import store from '@/store'
import { useLambda } from '@/views/habit/awsLambda'
import endpoints from '@/libs/endpoints'
import i18n from '@/libs/i18n'
import { oid, date } from '@/helpers/mongoHelpers'
import { LOGO_CORREO } from '@/libs/emailAssets'
import useAuditLog from '@/composables/useAuditLog'

export const RIGHTS = [
  { code: 'ACC', labelKey: 'arsopbl.right_access', descKey: 'arsopbl.right_access_desc' },
  { code: 'RECT', labelKey: 'arsopbl.right_rectification', descKey: 'arsopbl.right_rectification_desc' },
  { code: 'SUPR', labelKey: 'arsopbl.right_suppression', descKey: 'arsopbl.right_suppression_desc' },
  { code: 'OPO', labelKey: 'arsopbl.right_opposition', descKey: 'arsopbl.right_opposition_desc' },
  { code: 'PORT', labelKey: 'arsopbl.right_portability', descKey: 'arsopbl.right_portability_desc' },
  { code: 'BLOQ', labelKey: 'arsopbl.right_blocking', descKey: 'arsopbl.right_blocking_desc' },
  { code: 'LIMI', labelKey: 'arsopbl.right_limitation', descKey: 'arsopbl.right_limitation_desc' },
]

export const STAGES = {
  reception: 1,
  identity_validation: 2,
  analysis: 3,
  execution: 4,
  response: 5,
  closure: 6,
}

export default function useCommonArsopbl() {
  const {
    createItem, updateItem, getItem, getItems, getItemsAggregate,
    getPresignedUploadUrl, getPresignedDownloadUrl, sendEmail,
  } = useLambda()
  const userData = store.state?.userStore?.userData
  const collection = 'arsopbl'
  const { createInitialAuditEntry, addAuditEntry: addAuditEntryUnified } = useAuditLog()

  const generateCaseNumber = () => {
    const now = new Date()
    const year = now.getFullYear()
    const random = Math.floor(Math.random() * 900000 + 100000)
    return `ARSOPBL-${year}-${random}`
  }

  const generateFollowUpToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let token = ''
    for (let i = 0; i < 32; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return token
  }

  const uploadFileToS3 = async (file, clientId) => {
    const destinationFolder = `${clientId}/arsopbl`
    const { uploadUrl, key } = await getPresignedUploadUrl({
      fileName: file.name,
      fileType: file.type,
      destinationFolder,
    })

    await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    })

    return key
  }

  const downloadFile = async (fileKey) => {
    const { url } = await getPresignedDownloadUrl({ key: fileKey })
    window.open(url, '_blank')
  }

  const createRequest = async (payload, identityFile, clientId) => {
    const caseNumber = generateCaseNumber()
    const followUpToken = generateFollowUpToken()

    const requestPayload = {
      ...payload,
      client_id: oid(clientId),
      caseNumber,
      followUpToken,
      stage: 'reception',
      status: 'open',
      createdAt: date(new Date()),
      history: [createInitialAuditEntry()], // Sistema unificado
    }

    const { insertedId } = await createItem({ collection, payload: requestPayload })
    const requestId = insertedId.toString()

    if (identityFile) {
      const key = await uploadFileToS3(identityFile, clientId)
      await updateItem({
        collection,
        query: { _id: oid(requestId) },
        action: { $set: { identityDocumentKey: key } },
      })
    }

    return { requestId, caseNumber, followUpToken }
  }

  const advanceStage = async (requestId, newStage, updateData = {}, originalData = null) => {
    if (updateData.executionResponsible) {
      updateData.executionResponsible = oid(updateData.executionResponsible)
    }

    const payload = {
      stage: newStage,
      ...updateData,
    }

    await updateItem({
      collection,
      query: { _id: oid(requestId), client_id: oid(userData.client.$oid) },
      action: { $set: payload },
    })

    // Registrar cambio de etapa con detección de campos
    if (originalData) {
      await addAuditEntryUnified({
        collection,
        documentId: oid(requestId),
        originalData,
        updatedData: payload,
        action: `cambio_etapa_${newStage}`,
        excludeFields: ['stage'], // La etapa ya está implícita en el action
      })
    }
  }

  const closeRequest = async (requestId, closureStatus, closureData = {}, originalData = null) => {
    const payload = {
      stage: 'closure',
      status: closureStatus,
      closedAt: date(new Date()),
      closedBy: oid(userData.worker_id?.$oid || userData._id),
      ...closureData,
    }

    await updateItem({
      collection,
      query: { _id: oid(requestId), client_id: oid(userData.client.$oid) },
      action: { $set: payload },
    })

    // Registrar cierre con detección de campos
    if (originalData) {
      await addAuditEntryUnified({
        collection,
        documentId: oid(requestId),
        originalData,
        updatedData: payload,
        action: `cerrar_${closureStatus}`,
        excludeFields: ['closedAt', 'closedBy'], // Metadatos internos
      })
    }
  }

  const getConfirmationEmailTemplate = ({ recipientName, caseNumber: cNumber, rightCode, followUpUrl }) => `
    <html>
      <body style="margin: 0; padding: 0; background-color: #ffffff;">
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #ffffff;">
          <tr>
            <td align="center" style="padding: 20px 10px;">
              <table width="600" cellpadding="0" cellspacing="0" style="border-collapse: collapse; border-radius: 10px; overflow: hidden;">
                <tr>
                  <td style="background-color: #ecf0f1; text-align: center; padding: 20px 0 10px;">
                    <img width="120" src="${LOGO_CORREO}" alt="AddCo Logo">
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #ecf0f1; padding: 20px 40px 30px; color: #34495e; font-family: sans-serif;">
                    <h2 style="color: #498ceb; margin: 0 0 7px;">
                      ${i18n.t('arsopbl.email_hello')}${recipientName ? ` ${recipientName}` : ''},
                    </h2>
                    <p style="margin: 10px 0; font-size: 15px;">
                      ${i18n.t('arsopbl.email_confirmation_text')}
                    </p>
                    <div style="background-color: white; border-left: 4px solid #498ceb; padding: 15px; margin: 15px 0; border-radius: 5px;">
                      <ul style="font-size: 15px; margin: 10px 0; padding-left: 20px;">
                        <li><u>${i18n.t('arsopbl.case_number')}</u>: <b>${cNumber}</b></li>
                        <li><u>${i18n.t('arsopbl.right_exercised')}</u>: <b>${i18n.t(`arsopbl.right_${rightCode.toLowerCase()}`)}</b></li>
                        <li><u>${i18n.t('arsopbl.estimated_response')}</u>: <b>20 ${i18n.t('arsopbl.business_days')}</b></li>
                      </ul>
                    </div>
                    <p style="margin: 10px 0; font-size: 14px; color: #7f8c8d;">
                      ${i18n.t('arsopbl.email_follow_up_instruction')}
                    </p>
                    <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0 10px;">
                      <tr>
                        <td align="center">
                          <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #498ceb; font-size: 17px; display: inline-block;" href="${followUpUrl}">
                            ${i18n.t('arsopbl.email_follow_up_button')}
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #34495e; text-align: center; padding: 15px;">
                    <p style="color: #ecf0f1; font-size: 12px; margin: 0;">
                      Canal ARSOPB+L — Ejercicio de Derechos de Datos Personales
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `

  const sendConfirmationEmail = async ({ recipientEmail, recipientName, caseNumber: cNumber, rightCode, clientPublicId, followUpToken }) => {
    const followUpUrl = `${endpoints.app_url}/arsopbl/follow-up/${clientPublicId}?token=${followUpToken}`
    const htmlBody = getConfirmationEmailTemplate({ recipientName, caseNumber: cNumber, rightCode, followUpUrl })
    const subject = `${i18n.t('arsopbl.email_subject_confirmation')} — ${cNumber}`

    await sendEmail({ to: [recipientEmail], subject, htmlBody })
  }

  const sendNewRequestNotification = async ({ caseNumber: cNumber, rightCode, titularName, clientId }) => {
    const workers = await getItems({
      collection: 'worker',
      query: {
        client_id: oid(clientId),
        deleted: { $ne: true },
        role: { $in: ['consultant', 'manager'] },
        email: { $exists: true, $ne: null },
      },
    })

    if (!workers?.length) return

    const htmlBody = `
      <html>
        <body style="margin: 0; padding: 0; background-color: #ffffff;">
          <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #ffffff;">
            <tr>
              <td align="center" style="padding: 20px 10px;">
                <table width="600" cellpadding="0" cellspacing="0" style="border-collapse: collapse; border-radius: 10px; overflow: hidden;">
                  <tr>
                    <td style="background-color: #ecf0f1; text-align: center; padding: 20px 0 10px;">
                      <img width="120" src="${LOGO_CORREO}" alt="AddCo Logo">
                    </td>
                  </tr>
                  <tr>
                    <td style="background-color: #ecf0f1; padding: 20px 40px 30px; color: #34495e; font-family: sans-serif;">
                      <h2 style="color: #e67e22; margin: 0 0 7px;">
                        🔔 ${i18n.t('arsopbl.email_new_request_title')}
                      </h2>
                      <p style="margin: 10px 0; font-size: 15px;">
                        ${i18n.t('arsopbl.email_new_request_text')}
                      </p>
                      <div style="background-color: white; border-left: 4px solid #e67e22; padding: 15px; margin: 15px 0; border-radius: 5px;">
                        <ul style="font-size: 15px; margin: 10px 0; padding-left: 20px;">
                          <li><u>${i18n.t('arsopbl.case_number')}</u>: <b>${cNumber}</b></li>
                          <li><u>${i18n.t('arsopbl.right_exercised')}</u>: <b>${i18n.t(`arsopbl.right_${rightCode.toLowerCase()}`)}</b></li>
                          <li><u>${i18n.t('arsopbl.titular')}</u>: <b>${titularName}</b></li>
                        </ul>
                      </div>
                      <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0 10px;">
                        <tr>
                          <td align="center">
                            <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #498ceb; font-size: 17px; display: inline-block;" href="${endpoints.app_url}/privacy/arsopbl">
                              ${i18n.t('arsopbl.email_see_request')}
                            </a>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                  <tr>
                    <td style="background-color: #34495e; text-align: center; padding: 15px;">
                      <p style="color: #ecf0f1; font-size: 12px; margin: 0;">
                        Canal ARSOPB+L — Ejercicio de Derechos de Datos Personales
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `

    const subject = `${i18n.t('arsopbl.email_subject_new_request')} — ${cNumber}`
    const recipients = workers.map(w => w.realEmail || w.email)

    await sendEmail({ to: recipients, subject, htmlBody })
  }

  // Generic notification template
  const getNotificationTemplate = ({ accentColor, title, greeting, bodyText, rows = [], contentBlock, buttonUrl, buttonText }) => `
    <html>
      <body style="margin: 0; padding: 0; background-color: #ffffff;">
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #ffffff;">
          <tr>
            <td align="center" style="padding: 20px 10px;">
              <table width="600" cellpadding="0" cellspacing="0" style="border-collapse: collapse; border-radius: 10px; overflow: hidden;">
                <tr>
                  <td style="background-color: #ecf0f1; text-align: center; padding: 20px 0 10px;">
                    <img width="120" src="${LOGO_CORREO}" alt="AddCo Logo">
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #ecf0f1; padding: 20px 40px 30px; color: #34495e; font-family: sans-serif;">
                    <h2 style="color: ${accentColor}; margin: 0 0 7px;">
                      ${title}
                    </h2>
                    ${greeting ? `<p style="margin: 10px 0; font-size: 15px;">${greeting}</p>` : ''}
                    <p style="margin: 10px 0; font-size: 15px;">
                      ${bodyText}
                    </p>
                    ${rows.length ? `
                    <div style="background-color: white; border-left: 4px solid ${accentColor}; padding: 15px; margin: 15px 0; border-radius: 5px;">
                      <ul style="font-size: 15px; margin: 10px 0; padding-left: 20px;">
                        ${rows.map(r => `<li><u>${r.label}</u>: <b>${r.value}</b></li>`).join('')}
                      </ul>
                    </div>` : ''}
                    ${contentBlock ? `
                    <div style="margin: 15px 0;">
                      <p style="margin: 5px 0; font-size: 14px; color: #7f8c8d;"><u>${contentBlock.label}</u>:</p>
                      <div style="background-color: white; border: 1px solid #dfe3e6; padding: 15px; border-radius: 5px; font-size: 14px;">
                        ${contentBlock.value}
                      </div>
                    </div>` : ''}
                    ${buttonUrl ? `
                    <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0 10px;">
                      <tr>
                        <td align="center">
                          <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #498ceb; font-size: 17px; display: inline-block;" href="${buttonUrl}">
                            ${buttonText}
                          </a>
                        </td>
                      </tr>
                    </table>` : ''}
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #34495e; text-align: center; padding: 15px;">
                    <p style="color: #ecf0f1; font-size: 12px; margin: 0;">
                      Canal ARSOPB+L — Ejercicio de Derechos de Datos Personales
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `

  // Milestone stages that trigger a data subject notification
  const TITULAR_NOTIFY_STAGES = ['identity_validation', 'analysis', 'execution', 'response']

  const sendStageChangeNotification = async ({ request, newStage }) => {
    const titularEmail = request?.titular?.email
    if (!titularEmail || !TITULAR_NOTIFY_STAGES.includes(newStage)) return

    const followUpUrl = request.clientPublicId && request.followUpToken
      ? `${endpoints.app_url}/arsopbl/follow-up/${request.clientPublicId}?case=${request.caseNumber}&token=${request.followUpToken}`
      : null

    const htmlBody = getNotificationTemplate({
      accentColor: '#498ceb',
      title: `📋 ${i18n.t('arsopbl.email_stage_change_title')}`,
      greeting: `${i18n.t('arsopbl.email_hello')}${request.titular.fullName ? ` ${request.titular.fullName}` : ''},`,
      bodyText: i18n.t('arsopbl.email_stage_change_text'),
      rows: [
        { label: i18n.t('arsopbl.case_number'), value: request.caseNumber },
        { label: i18n.t('arsopbl.right_exercised'), value: i18n.t(`arsopbl.right_${(request.rightCode || '').toLowerCase()}`) },
        { label: i18n.t('arsopbl.current_stage'), value: i18n.t(`arsopbl.stage_${newStage}`) },
      ],
      buttonUrl: followUpUrl,
      buttonText: i18n.t('arsopbl.email_follow_up_button'),
    })

    const subject = `${i18n.t('arsopbl.email_subject_stage_change')} — ${request.caseNumber}`
    await sendEmail({ to: [titularEmail], subject, htmlBody })
  }

  const sendResponsibleAssignedNotification = async ({ request, responsibleId }) => {
    if (!responsibleId) return
    const worker = await getItem({
      collection: 'worker',
      query: { _id: oid(responsibleId), deleted: { $ne: true } },
    })
    const recipient = worker?.realEmail || worker?.email
    if (!recipient) return

    const htmlBody = getNotificationTemplate({
      accentColor: '#e67e22',
      title: `🔔 ${i18n.t('arsopbl.email_assigned_title')}`,
      greeting: `${i18n.t('arsopbl.email_hello')}${worker.name ? ` ${worker.name}` : ''},`,
      bodyText: i18n.t('arsopbl.email_assigned_text'),
      rows: [
        { label: i18n.t('arsopbl.case_number'), value: request.caseNumber },
        { label: i18n.t('arsopbl.right_exercised'), value: i18n.t(`arsopbl.right_${(request.rightCode || '').toLowerCase()}`) },
        { label: i18n.t('arsopbl.titular'), value: (request.titular && request.titular.fullName) || '-' },
      ],
      buttonUrl: `${endpoints.app_url}/privacy/arsopbl/detail/${request._id}`,
      buttonText: i18n.t('arsopbl.email_see_request'),
    })

    const subject = `${i18n.t('arsopbl.email_subject_assigned')} — ${request.caseNumber}`
    await sendEmail({ to: [recipient], subject, htmlBody })
  }

  const sendClosureNotification = async ({ request, closureStatus, formalResponse, rejectionReason }) => {
    const titularEmail = request?.titular?.email
    if (!titularEmail) return

    const resolved = closureStatus === 'resolved'
    const followUpUrl = request.clientPublicId && request.followUpToken
      ? `${endpoints.app_url}/arsopbl/follow-up/${request.clientPublicId}?case=${request.caseNumber}&token=${request.followUpToken}`
      : null

    let contentBlock = null
    if (resolved && formalResponse) {
      contentBlock = { label: i18n.t('arsopbl.formal_response'), value: formalResponse }
    } else if (!resolved && rejectionReason) {
      contentBlock = { label: i18n.t('arsopbl.rejection_reason'), value: rejectionReason }
    }

    const htmlBody = getNotificationTemplate({
      accentColor: resolved ? '#28a745' : '#dc3545',
      title: `${resolved ? '✅' : '❌'} ${i18n.t(resolved ? 'arsopbl.email_resolved_title' : 'arsopbl.email_rejected_title')}`,
      greeting: `${i18n.t('arsopbl.email_hello')}${request.titular.fullName ? ` ${request.titular.fullName}` : ''},`,
      bodyText: i18n.t(resolved ? 'arsopbl.email_resolved_text' : 'arsopbl.email_rejected_text'),
      rows: [
        { label: i18n.t('arsopbl.case_number'), value: request.caseNumber },
        { label: i18n.t('arsopbl.right_exercised'), value: i18n.t(`arsopbl.right_${(request.rightCode || '').toLowerCase()}`) },
      ],
      contentBlock,
      buttonUrl: followUpUrl,
      buttonText: i18n.t('arsopbl.email_follow_up_button'),
    })

    const subject = `${i18n.t('arsopbl.email_subject_closure')} — ${request.caseNumber}`
    await sendEmail({ to: [titularEmail], subject, htmlBody })
  }

  const businessDaysDiff = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)

    let count = 0
    const current = new Date(start)

    if (end >= start) {
      while (current < end) {
        current.setDate(current.getDate() + 1)
        const day = current.getDay()
        if (day !== 0 && day !== 6) count++
      }
    } else {
      while (current > end) {
        const day = current.getDay()
        if (day !== 0 && day !== 6) count++
        current.setDate(current.getDate() - 1)
      }
    }

    return count
  }

  const getDeadlineStatus = (createdAt) => {
    const days = businessDaysDiff(createdAt, new Date())
    if (days <= 15) return { variant: 'success', label: 'on_time', days }
    if (days <= 18) return { variant: 'warning', label: 'near_deadline', days }
    if (days <= 20) return { variant: 'danger', label: 'urgent', days }
    return { variant: 'dark', label: 'overdue', days }
  }

  // Response templates CRUD
  const templateCollection = 'arsopblTemplate'

  const getTemplates = async () => getItems({
    collection: templateCollection,
    query: { client_id: oid(userData.client.$oid), deleted: { $ne: true } },
    options: { sort: { name: 1 } },
  })

  const createTemplate = async ({ name, rightCode, content }) => createItem({
    collection: templateCollection,
    payload: {
      client_id: oid(userData.client.$oid),
      name,
      rightCode: rightCode || null,
      content,
      createdAt: date(new Date()),
    },
  })

  const updateTemplate = async (templateId, { name, rightCode, content }) => updateItem({
    collection: templateCollection,
    query: { _id: oid(templateId), client_id: oid(userData.client.$oid) },
    action: { $set: { name, rightCode: rightCode || null, content } },
  })

  const deleteTemplate = async templateId => updateItem({
    collection: templateCollection,
    query: { _id: oid(templateId), client_id: oid(userData.client.$oid) },
    action: { $set: { deleted: true } },
  })

  return {
    collection,
    createRequest,
    advanceStage,
    closeRequest,
    uploadFileToS3,
    downloadFile,
    sendConfirmationEmail,
    sendNewRequestNotification,
    sendStageChangeNotification,
    sendResponsibleAssignedNotification,
    sendClosureNotification,
    businessDaysDiff,
    getDeadlineStatus,
    generateCaseNumber,
    getTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  }
}
